import { useMemo, useState } from "react";
import { PageHeader, Reveal } from "@/components/common/Reveal";
import { ProjectCard } from "@/components/projects/ProjectCard";
import { CtaBand } from "@/components/sections/CtaBand";
import { PROJECTS } from "@/data/site";

const TYPES = [["all", "All types"], ["houses", "Houses"], ["apartments", "Apartments"], ["mixed", "Mixed"]];
const STATUSES = [["all", "All"], ["completed", "Completed"], ["ongoing", "Ongoing"]];

const FilterGroup = ({ label, options, value, onChange, prefix }) => (
  <div className="flex flex-wrap items-center gap-2">
    <span className="label-mono mr-2 text-steel">{label}</span>
    {options.map(([v, l]) => (
      <button key={v} onClick={() => onChange(v)} aria-pressed={value === v} data-testid={`${prefix}-${v}`}
        className={`h-10 border px-4 font-display text-sm font-semibold uppercase tracking-[0.1em] transition-colors duration-300 ${value === v ? "border-brand bg-brand text-canvas" : "border-canvas-line text-ink hover:border-brand"}`}>
        {l}
      </button>
    ))}
  </div>
);

export default function Projects() {
  const [type, setType] = useState("all");
  const [status, setStatus] = useState("all");
  const list = useMemo(
    () => PROJECTS.filter((p) => (type === "all" || p.type === type) && (status === "all" || p.status === status)),
    [type, status]
  );

  return (
    <main data-testid="projects-page">
      <PageHeader testid="projects-header" label="Projects" title="Schemes we've fitted out"
        text="We work with established developers and main contractors to deliver the mechanical fit-out of high-quality housing and apartment schemes in Dublin and surrounding counties." />
      <section className="mx-auto max-w-7xl px-5 py-16 sm:px-8 sm:py-20">
        <div className="flex flex-col gap-5 border-b border-canvas-line pb-8 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-col gap-4 xl:flex-row xl:gap-10">
            <FilterGroup label="Type" options={TYPES} value={type} onChange={setType} prefix="filter-type" />
            <FilterGroup label="Status" options={STATUSES} value={status} onChange={setStatus} prefix="filter-status" />
          </div>
          <p className="label-mono text-steel" data-testid="projects-count">{String(list.length).padStart(2, "0")} / {PROJECTS.length} schemes</p>
        </div>
        {list.length === 0 ? (
          <p className="py-24 text-center text-steel" data-testid="projects-empty">No schemes match these filters.</p>
        ) : (
          <div className="mt-14 grid grid-cols-1 gap-x-8 gap-y-16 md:grid-cols-2 lg:grid-cols-3" data-testid="projects-grid">
            {list.map((p, i) => (
              <Reveal key={p.slug} delay={(i % 3) * 0.06}>
                <ProjectCard project={p} />
              </Reveal>
            ))}
          </div>
        )}
      </section>
      <CtaBand testid="projects-cta" />
    </main>
  );
}
